import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} [previousButton] - Previous media button.
 * @property {HTMLButtonElement} [nextButton] - Next media button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #scrollTarget = /** @type {HTMLElement | HTMLDialogElement | null} */ (null);
  #activeIndex = 0;
  #globalLogo = /** @type {HTMLElement | null} */ (null);
  #globalLogoParent = /** @type {Node | null} */ (null);
  #globalLogoNextSibling = /** @type {Node | null} */ (null);
  #mobileLoopInProgress = false;
  /** @type {{ x: number, y: number, index: number } | null} */
  #mobileTouchGesture = null;

  connectedCallback() {
    super.connectedCallback();
    const mobileGallery = this.refs.dialog.querySelector('.dialog-zoomed-gallery');
    this.#scrollTarget =
      window.matchMedia('(max-width: 749px)').matches && mobileGallery instanceof HTMLElement
        ? mobileGallery
        : this.refs.dialog;
    this.#scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
    if (this.#scrollTarget instanceof HTMLElement && mobileGallery === this.#scrollTarget) {
      this.#scrollTarget.addEventListener('touchstart', this.#handleMobileTouchStart, { passive: true });
      this.#scrollTarget.addEventListener('touchend', this.#handleMobileTouchEnd);
      this.#scrollTarget.addEventListener('touchcancel', this.#resetMobileTouchGesture);
    }
  }

  disconnectedCallback() {
    this.#restoreGlobalLogo();
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.#scrollTarget?.removeEventListener('touchstart', this.#handleMobileTouchStart);
    this.#scrollTarget?.removeEventListener('touchend', this.#handleMobileTouchEnd);
    this.#scrollTarget?.removeEventListener('touchcancel', this.#resetMobileTouchGesture);
    this.#scrollTarget = null;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      // Let the dialog run its native initial-focus step before the persistent
      // wordmark is moved inside it. If the logo is prepended first, Safari
      // treats it as the modal's first focusable control and draws a large
      // focus rectangle around it. Both operations remain in the same task,
      // so the logo is still mounted before the browser can paint a frame.
      dialog.showModal();
      this.#mountGlobalLogo();
      this.#clearMobileTouchFocus();
      this.#activeIndex = index;
      this.#updateNavigationState(index);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (this.#mobileLoopInProgress) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = this.#isMobileHorizontalGallery()
      ? this.#getClosestMobileMedia()
      : await getMostVisibleElement(media);
    if (!mostVisibleElement) return;

    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#activeIndex = activeIndex;
    this.#updateNavigationState(activeIndex);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Selects the previous media item without closing the zoom gallery.
   * @param {MouseEvent} event
   */
  previous(event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectThumbnail(this.#activeIndex - 1);
  }

  /**
   * Selects the next media item without closing the zoom gallery.
   * @param {MouseEvent} event
   */
  next(event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectThumbnail(this.#activeIndex + 1);
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#clearMobileTouchFocus();
    dialog.close();
    this.#restoreGlobalLogo();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    const mediaCount = this.refs.thumbnails.children.length;
    const isMobileLoop = this.#isMobileHorizontalGallery() && (index < 0 || index >= mediaCount);

    // Keep the original bounded desktop behavior. On mobile, normalize only
    // the two edge requests so arrows and outward swipes remain continuous.
    if (isNaN(index) || (!isMobileLoop && (index < 0 || index >= mediaCount))) return;

    const requestedIndex = index;
    if (isMobileLoop) index = ((index % mediaCount) + mediaCount) % mediaCount;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    this.#activeIndex = index;
    this.#updateNavigationState(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      if (this.#isMobileHorizontalGallery() && this.#scrollTarget instanceof HTMLElement) {
        if (isMobileLoop) {
          await this.#loopMobileGallery(targetImage, requestedIndex < 0 ? -1 : 1, options.behavior);
        } else {
          // scrollIntoView() may choose the dialog/page instead of the
          // horizontal gallery after the first move on mobile Safari. Keep the
          // operation on the actual scroll owner so 1 -> 2 -> 1 remains
          // repeatable.
          this.#scrollTarget.scrollTo({
            behavior: options.behavior,
            left: targetImage.offsetLeft,
            top: 0,
          });
        }
      } else {
        targetImage.scrollIntoView({
          behavior: options.behavior,
        });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Keeps the minimalist zoom arrows in sync with the active media.
   * @param {number} index
   */
  #updateNavigationState(index) {
    const mediaCount = this.refs.media.length;
    const mobileLoopEnabled = this.#isMobileHorizontalGallery() && mediaCount > 1;
    if (this.refs.previousButton) this.refs.previousButton.disabled = !mobileLoopEnabled && index <= 0;
    if (this.refs.nextButton) this.refs.nextButton.disabled = !mobileLoopEnabled && index >= mediaCount - 1;
  }

  /**
   * Animates one additional, pixel-identical item in the requested direction,
   * then restores the original gallery before the browser paints another
   * frame. The clone never carries Component refs, so it cannot corrupt the
   * live media collection.
   * @param {HTMLElement} targetImage
   * @param {-1 | 1} direction
   * @param {ScrollBehavior} behavior
   */
  async #loopMobileGallery(targetImage, direction, behavior) {
    const gallery = this.#scrollTarget;
    const currentImage = direction < 0 ? this.refs.media[0] : this.refs.media[this.refs.media.length - 1];
    if (!(gallery instanceof HTMLElement) || !currentImage || this.#mobileLoopInProgress) return;

    this.#mobileLoopInProgress = true;

    const clone = /** @type {HTMLElement} */ (targetImage.cloneNode(true));
    clone.setAttribute('data-mobile-loop-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('id');
    clone.removeAttribute('ref');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));

    if (direction < 0) {
      gallery.prepend(clone);
      // Inserting before the first item changes every original offset. Keep
      // the currently visible first item fixed before starting the animation.
      gallery.scrollTo({ left: currentImage.offsetLeft, top: 0, behavior: 'instant' });
    } else {
      gallery.append(clone);
    }

    await nextAnimationFrame();
    gallery.scrollTo({ left: clone.offsetLeft, top: 0, behavior });

    if (behavior !== 'instant') await waitForScrollCompletion(gallery);

    // The clone and target are visually identical. Restoring both operations
    // in the same task prevents a visible jump at the loop boundary.
    clone.remove();
    gallery.scrollTo({ left: targetImage.offsetLeft, top: 0, behavior: 'instant' });
    this.#mobileLoopInProgress = false;
  }

  #handleMobileTouchStart = (event) => {
    if (event.touches.length !== 1 || this.#mobileLoopInProgress) return;

    const touch = event.touches[0];
    this.#mobileTouchGesture = { x: touch.clientX, y: touch.clientY, index: this.#activeIndex };
  };

  #handleMobileTouchEnd = (event) => {
    const gesture = this.#mobileTouchGesture;
    this.#mobileTouchGesture = null;
    if (!gesture || event.changedTouches.length !== 1 || this.#mobileLoopInProgress) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - gesture.x;
    const deltaY = touch.clientY - gesture.y;
    const galleryWidth = this.#scrollTarget instanceof HTMLElement ? this.#scrollTarget.clientWidth : 0;
    const minimumDistance = Math.max(36, galleryWidth * 0.08);

    if (Math.abs(deltaX) < minimumDistance || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (this.#activeIndex !== gesture.index) return;

    const lastIndex = this.refs.media.length - 1;
    if (gesture.index === lastIndex && deltaX < 0) {
      event.preventDefault();
      void this.selectThumbnail(lastIndex + 1);
    } else if (gesture.index === 0 && deltaX > 0) {
      event.preventDefault();
      void this.selectThumbnail(-1);
    }
  };

  #resetMobileTouchGesture = () => {
    this.#mobileTouchGesture = null;
  };

  #mountGlobalLogo() {
    const { dialog } = this.refs;
    const logo = document.querySelector('.stylist-global-logo');

    if (!(logo instanceof HTMLElement) || dialog.contains(logo)) return;

    if (document.activeElement === logo) logo.blur();

    this.#globalLogo = logo;
    this.#globalLogoParent = logo.parentNode;
    this.#globalLogoNextSibling = logo.nextSibling;
    dialog.prepend(logo);
  }

  #clearMobileTouchFocus() {
    if (!window.matchMedia('(max-width: 749px) and (pointer: coarse)').matches) return;

    const { dialog } = this.refs;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && dialog.contains(activeElement)) {
      activeElement.blur();
    }
  }

  #restoreGlobalLogo() {
    const logo = this.#globalLogo;
    const parent = this.#globalLogoParent;
    const nextSibling = this.#globalLogoNextSibling;

    if (!logo) return;

    if (parent?.isConnected) {
      if (nextSibling?.parentNode === parent) {
        parent.insertBefore(logo, nextSibling);
      } else {
        parent.appendChild(logo);
      }
    } else {
      document.body.prepend(logo);
    }

    this.#globalLogo = null;
    this.#globalLogoParent = null;
    this.#globalLogoNextSibling = null;
  }

  #isMobileHorizontalGallery() {
    return (
      window.matchMedia('(max-width: 749px)').matches &&
      this.#scrollTarget instanceof HTMLElement &&
      this.#scrollTarget.classList.contains('dialog-zoomed-gallery')
    );
  }

  /**
   * Resolves the current item from the horizontal scroll position without an
   * asynchronous viewport observer. This avoids stale observer callbacks
   * changing the active arrow state after a second swipe.
   * @returns {HTMLElement | null}
   */
  #getClosestMobileMedia() {
    if (!(this.#scrollTarget instanceof HTMLElement)) return null;

    const viewportCenter =
      this.#scrollTarget.scrollLeft + this.#scrollTarget.clientWidth / 2;

    return (
      this.refs.media.reduce(
        (closest, media) => {
          const mediaCenter = media.offsetLeft + media.offsetWidth / 2;
          const distance = Math.abs(mediaCenter - viewportCenter);
          return distance < closest.distance ? { media, distance } : closest;
        },
        { media: /** @type {HTMLElement | null} */ (null), distance: Number.POSITIVE_INFINITY }
      ).media || null
    );
  }
}

function nextAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
}

/**
 * Resolves after native smooth scrolling settles, with a bounded fallback for
 * Safari versions that do not expose the scrollend event.
 * @param {HTMLElement} element
 */
function waitForScrollCompletion(element) {
  return new Promise((resolve) => {
    let debounceTimer;
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(debounceTimer);
      clearTimeout(fallbackTimer);
      element.removeEventListener('scroll', handleScroll);
      element.removeEventListener('scrollend', finish);
      resolve(undefined);
    };

    const handleScroll = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(finish, 90);
    };

    const fallbackTimer = setTimeout(finish, 1000);
    element.addEventListener('scroll', handleScroll, { passive: true });
    element.addEventListener('scrollend', finish, { once: true });
  });
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
